import React from 'react';

import { Button } from '@cowbell/ui';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { Show } from '../../../Show';
import classes from './OrgSaveBar.module.css';

interface OrgSaveBarProps {
  readonly visible: boolean;
  readonly saving: boolean;
  readonly missingCount: number;
  readonly onSave: () => void;
  readonly onDiscard: () => void;
}

const message = (missingCount: number): string => {
  if (missingCount === 0) {
    return 'You have unsaved changes';
  }

  if (missingCount === 1) {
    return '1 required field still needs a value';
  }

  return `${missingCount} required fields still need a value`;
};

/**
 * The page's single save affordance, shown once either form is dirty.
 *
 * <p>A missing-required count is surfaced but deliberately does NOT disable
 * Save, which is where this departs from the supplied design. The
 * physical-address fields carry no client-side required check today, so
 * blocking on the count would lock exactly the policyholders with incomplete
 * records out of editing -- the complaint this rewrite exists to fix
 * (FIR-21111). Each form's own resolver still gates its own submit.
 */
export const OrgSaveBar = ({
  visible,
  saving,
  missingCount,
  onSave,
  onDiscard,
}: OrgSaveBarProps) => {
  if (!visible) {
    return null;
  }

  return (
    <div className={classes.bar}>
      <div className={classes.inner}>
        <p className={classes.message}>
          <Show when={missingCount > 0}>
            <WarningAmberIcon
              fontSize="small"
              color="warning"
              aria-hidden="true"
            />
          </Show>
          {message(missingCount)}
        </p>
        <div className={classes.actions}>
          <Button variant="default" onClick={onDiscard}>
            Discard
          </Button>
          <Button
            type="button"
            loading={saving}
            disabled={saving}
            onClick={onSave}
          >
            {saving ? 'Saving' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrgSaveBar;
